import {
    APPOINTMENT_START_STOP_DATE_TIME_FORMAT,
    DEFAULT_TIME_FORMAT,
    MILITARY_TIME_FORMAT,
    stringToDate,
    stringToDateWithFormat
} from "./formatUtils";
import {DateTime} from "luxon";

export const isStopTimeAfterStartTime = (startTime, endTime, startDateTimeAsDate, endDateTimeAsDate) => {
    if (!isTimeValid(startTime) || !isTimeValid(endTime)){
        return false;
    }

    const now = DateTime.now();
    const startDate = startDateTimeAsDate ? startDateTimeAsDate : now;
    const endDate = endDateTimeAsDate ? endDateTimeAsDate : now;
    const startDateTime = combineDateAndTime(startDate, startTime).toMillis();
    const endDateTime = combineDateAndTime(endDate, endTime).toMillis();

    return endDateTime <= startDateTime;
};

export const isTimeValid = (timeText) => {
    const time = DateTime.fromFormat(timeText, DEFAULT_TIME_FORMAT);

    return time.isValid;
};

export const validateTime = (timeText) => {
    const time = DateTime.fromFormat(timeText, DEFAULT_TIME_FORMAT);

    return time.isValid ? time : null;
};

export const getTimeInstanceFromString = (time) => {
    let timeInstance = null;

    if (time) {
        if (time.length === 8){
            timeInstance = DateTime.fromFormat(time, DEFAULT_TIME_FORMAT);
        } else if (time.length === 5){
            timeInstance = DateTime.fromFormat(time, MILITARY_TIME_FORMAT);
        } else {
            timeInstance = DateTime.fromFormat(time, APPOINTMENT_START_STOP_DATE_TIME_FORMAT);
        }
    }

    return timeInstance;
}

export const combineDateAndTime = (initialDate, time) => {
    const timeInstance = stringToDateWithFormat(time, DEFAULT_TIME_FORMAT);
    
    if (!timeInstance || !timeInstance.isValid) {
        return null;
    }

    return initialDate.startOf('day').set({
        hour: timeInstance.hour,
        minute: timeInstance.minute,
        second: 0,
        millisecond: 0
    });
}

export const getHoursTime = (time) => {
    if (typeof time === "string" && time.includes("M")) {
        return time;
    }
    
    if (time && time.toFormat) {
        return time.toFormat(DEFAULT_TIME_FORMAT);
    }
    
    return time.toLocaleString('en-US', { hour: '2-digit', minute:'2-digit', hour12: true });
}

export const validateCurrentTime = (time, date) => {
    const now = DateTime.now();
    
    const appointmentDate = stringToDate(date);
    if (!appointmentDate || !appointmentDate.isValid) {
        return false;
    }
    
    const timeInstance = getTimeInstanceFromString(time);
    if (!timeInstance || !timeInstance.isValid) {
        return false;
    }
    
    const appointmentDateTime = now.startOf('day').set({
        year: appointmentDate.year,
        month: appointmentDate.month,
        day: appointmentDate.day,
        hour: timeInstance.hour,
        minute: timeInstance.minute,
        second: 0,
        millisecond: 0
    });
    
    return appointmentDateTime > now;
}

export const validateCurrentDate = (date) => {
    const now = DateTime.now();
    
    const inputDate = stringToDate(date);
    if (!inputDate || !inputDate.isValid) {
        return false;
    }
    
    const todayStart = now.startOf('day');
    const inputDateStart = now.startOf('day').set({
        year: inputDate.year,
        month: inputDate.month,
        day: inputDate.day
    });
    
    return inputDateStart > todayStart;
}

/**
 * Creates a DateTime instance preserving the system timezone offset.
 * Useful for avoiding DST bugs when combining dates and times.
 * 
 * @param {Object} options
 * @param {number} options.year - Target year
 * @param {number} options.month - Target month (1-12)
 * @param {number} options.day - Target day
 * @param {number} options.hour - Target hour (0-23)
 * @param {number} options.minute - Target minute (0-59)
 * @param {number} options.second - Target second (0-59, optional)
 * @returns {DateTime} DateTime instance with system timezone preserved
 */
export const createDateTimePreservingOffset = ({
    year,
    month,
    day,
    hour = 0,
    minute = 0,
    second = 0
}) => {
    const now = DateTime.now();

    return now.startOf('day').set({
        year,
        month,
        day,
        hour,
        minute,
        second,
        millisecond: 0
    });
};

/**
 * Gets current DateTime with clean seconds/milliseconds.
 * Preserves system timezone offset (DST-safe).
 * 
 * @param {boolean} includeSeconds - Whether to include seconds (default: false)
 * @returns {DateTime} Current DateTime with system timezone
 */
export const nowAsDateTime = (includeSeconds = false) => {
    const now = DateTime.now();

    return now.startOf('day').set({
        hour: now.hour,
        minute: now.minute,
        second: includeSeconds ? now.second : 0,
        millisecond: 0
    });
};